import {
	BehaviorSubject,
	Observable,
	Subject,
	catchError,
	debounceTime,
	distinctUntilChanged,
	map,
	merge,
	of,
	switchMap,
} from 'rxjs';

import type { SellersFacade } from '@facades/sellers.facade';
import type { ISeller } from '@interfaces/seller';
import type { SubSink } from 'subsink';

export const SELLER_SEARCH_MIN_LENGTH = 2;
export const SELLER_SEARCH_DEBOUNCE_MS = 500;

export function normalizeSellerId(
	selectedSellerId?: number | string | null
): number | null {
	if (selectedSellerId == null || selectedSellerId === '') return null;

	const normalizedId = Number(selectedSellerId);
	return Number.isNaN(normalizedId) ? null : normalizedId;
}

export function resolveSelectedSellerId(
	formValue?: number | string | null,
	fallback?: number | string | null
): number | string | null {
	if (formValue != null && formValue !== '') return formValue;
	if (fallback != null && fallback !== '') return fallback;
	return null;
}

export function loadSellersForDropdown(
	sellersFacade: SellersFacade,
	selectedSellerId?: number | string | null,
	search?: string
): Observable<ISeller[]> {
	const normalizedId = normalizeSellerId(selectedSellerId);
	const params = search ? { search } : undefined;

	return sellersFacade.getSellers(params).pipe(
		switchMap(({ result }) => {
			const sellers = result ?? [];
			const hasSelected =
				normalizedId == null ||
				sellers.some(seller => seller.id === normalizedId);

			if (hasSelected) return of(sellers);

			return sellersFacade.getSellerById(normalizedId).pipe(
				map(selected => [...sellers, selected]),
				catchError(() => of(sellers))
			);
		})
	);
}

export class SellerDropdownSearch {
	private readonly search$ = new Subject<string | undefined>();
	private readonly sellersSubject = new BehaviorSubject<ISeller[]>([]);

	readonly sellers$ = this.sellersSubject.asObservable();

	constructor(
		private readonly sellersFacade: SellersFacade,
		private readonly getSelectedSellerId: () =>
			| number
			| string
			| null
			| undefined
	) {}

	init(subs: SubSink) {
		subs.add(
			merge(
				of(undefined),
				this.search$.pipe(debounceTime(SELLER_SEARCH_DEBOUNCE_MS))
			)
				.pipe(
					distinctUntilChanged(),
					switchMap(search =>
						loadSellersForDropdown(
							this.sellersFacade,
							this.getSelectedSellerId(),
							search
						)
					)
				)
				.subscribe(sellers => this.sellersSubject.next(sellers))
		);
	}

	onSearch(event: { filter: string }) {
		const search = event.filter?.trim() ?? '';

		if (!search) {
			this.search$.next(undefined);
			return;
		}

		if (search.length < SELLER_SEARCH_MIN_LENGTH) return;

		this.search$.next(search);
	}
}
